import { forwardRef, useEffect, useImperativeHandle } from "react"
import { TitlePageResponsiveIconSmall } from "../Titles/Titles"
import {
  faAddressBook,
  faAngleDown,
  faAngleUp,
  faCalendarWeek,
  faMailBulk,
  faNairaSign,
  faPhone,
  faRegistered,
  faUser,
} from "@fortawesome/free-solid-svg-icons"
import { ButtonIcon } from "../Button/Button"
import InputField from "../InputField/InputField"
import GetSpace from "../../utils/space"
import { useExpansive } from "../../hooks/useExpansive"
import { GenericDropDown } from "../Dropdown/Dropdown"
import { useCreatePerson } from "../../hooks/People/useCreatePerson"
import { PersonEntity } from "../../Objects/Person/PersonEntity"
import { SIZE } from "../../configuration/Sizes"
import THEME from "../../configuration/Theme"
import { ESexType } from "../../Enum/ESexType"
import { INPUT_SIZE } from "../../configuration/InputSize"
import { useTranslation } from "react-i18next"
import React from "react"

interface PersonComponentProps {
  personEntity?: PersonEntity
  disable?: boolean
  basic?: boolean
  initExpasive?: boolean
  headerActions?: React.ReactNode
}

export const PersonComponent = forwardRef((props: PersonComponentProps, ref) => {
  const { t } = useTranslation("person")

  const { expansive, handleChangeState } = useExpansive(props.initExpasive)

  const {
    name,
    cpf,
    rg,
    phone,
    email,
    dateBorn,
    gender,
    handleNameChange,
    handleCpfChange,
    handleRgChange,
    handlePhoneChange,
    handleEmailChange,
    handleDateBornChange,
    handleGenderChange,
    handleSubmitForm,
    handleInitialValue,
  } = useCreatePerson()

  useEffect(() => {
    handleInitialValue(props.personEntity)
  }, [props.personEntity])

  useImperativeHandle(ref, () => ({
    validate: () => {
      return handleSubmitForm()
    },
  }))

  return (
    <>
      <TitlePageResponsiveIconSmall
        title={t("title")}
        icon={faUser}
        boxShadow="none"
        children={
          <div className="flex items-center gap-2">
            {props.headerActions}

            <ButtonIcon
              sizeNumber={SIZE.buttonExpandIcon}
              bgColor={THEME.colors.buttonBGExpandColor}
              iconColor={THEME.colors.buttonFGExpandColor}
              typeButton="button"
              icon={!expansive ? faAngleDown : faAngleUp}
              onClick={handleChangeState}
              size="small"
              descriptionKey="interactionDescriptions:components.person.personComponent.button.handleChangeState"
            />
          </div>
        }
      />

      {expansive ? (
        <div className="p-2">
          <GetSpace top={15} />

          <GenericDropDown
            data={Object.mapEnumToStrings(ESexType)}
            initialState={gender}
            disable={props.disable}
            handlerFuncion={handleGenderChange}
            descriptionKey="interactionDescriptions:components.person.personComponent.select.select"
          />

          <GetSpace top={15} />

          <InputField
            maxLength={INPUT_SIZE.PERSON.name}
            type="text"
            placeholder={t("label_person_name_place_holder")}
            onInvalidMessage={t("label_person_name_onInvalidMessage")}
            icon={faNairaSign}
            disable={props.disable}
            value={name}
            onChange={handleNameChange}
            required
            descriptionKey="interactionDescriptions:components.person.personComponent.input.personNamePlaceHolder"
          />

          <GetSpace top={10} />

          <InputField
            maxLength={INPUT_SIZE.PERSON.cpf}
            minLength={INPUT_SIZE.PERSON.cpf}
            type="text"
            inputMode="numeric"
            pattern="[0-9]{11}"
            placeholder={t("label_person_cpf_place_holder")}
            onInvalidMessage={t("label_person_cpf_onInvalidMessage")}
            icon={faAddressBook}
            value={cpf}
            disable={props.disable}
            onChange={handleCpfChange}
            required
            descriptionKey="interactionDescriptions:components.person.personComponent.input.personCpfPlaceHolder"
          />

          {!props.basic && (
            <>
              <GetSpace top={10} />

              <InputField
                maxLength={INPUT_SIZE.PERSON.rg}
                type="number"
                placeholder={t("label_person_rg_place_holder")}
                onInvalidMessage={t("label_person_rg_onInvalidMessage")}
                icon={faRegistered}
                value={rg}
                disable={props.disable}
                onChange={handleRgChange}
                descriptionKey="interactionDescriptions:components.person.personComponent.input.personRgPlaceHolder"
              />

              <GetSpace top={10} />

              <InputField
                maxLength={INPUT_SIZE.PERSON.phone}
                type="number"
                placeholder={t("label_person_phone_place_holder")}
                onInvalidMessage={t("label_person_phone_onInvalidMessage")}
                icon={faPhone}
                disable={props.disable}
                value={phone}
                onChange={handlePhoneChange}
                descriptionKey="interactionDescriptions:components.person.personComponent.input.personPhonePlaceHolder"
              />

              <GetSpace top={10} />

              <InputField
                maxLength={INPUT_SIZE.PERSON.email}
                type="email"
                placeholder={t("label_person_email_place_holder")}
                onInvalidMessage={t("label_person_email_onInvalidMessage")}
                icon={faMailBulk}
                disable={props.disable}
                value={email}
                onChange={handleEmailChange}
                descriptionKey="interactionDescriptions:components.person.personComponent.input.personEmailPlaceHolder"
              />

              <GetSpace top={10} />

              <InputField
                maxLength={INPUT_SIZE.PERSON.dateBorn}
                type="date"
                disable={props.disable}
                placeholder={t("label_person_date_born_place_holder")}
                onInvalidMessage={t("label_person_date_born_onInvalidMessage")}
                icon={faCalendarWeek}
                value={dateBorn}
                onChange={handleDateBornChange}
                descriptionKey="interactionDescriptions:components.person.personComponent.input.personDateBornPlaceHolder"
              />
            </>
          )}
        </div>
      ) : null}
    </>
  )
})
